import { useEffect, useState } from 'react'
import { api, type Branch } from '../lib/api.ts'
import { BranchVerificationSwitch } from '../components/BranchVerificationSwitch.tsx'
import { Button } from '../components/Button.tsx'
import { DataTable, type Column } from '../components/DataTable.tsx'
import { icons } from '../components/icons.ts'
import { CAPABILITIES } from '../lib/capabilities.ts'

/**
 * The branches we know about, grouped by customer.
 *
 * What the ERP owns -- the code, the name, the city -- is not edited here,
 * which would invent a second copy of a list the pharmacy already maintains.
 * The one switch on a row is ours: whether we verify the branch at all
 * (#676). It is here as well as on the branch's page because onboarding is
 * going down a new customer's list switching off what is not a pharmacy.
 *
 * The column worth reading is `produtos`: the catalogue is account-wide and
 * identical for every row, so what differs is what each branch carries. A
 * branch reading zero is a membership sweep that has not run, which is
 * otherwise invisible -- it shows up as retrieval quietly falling back to the
 * whole account.
 */
function whenSynced(at: string | undefined): string {
  if (at === undefined) return 'nunca'
  return new Date(at).toLocaleString('pt-BR', { dateStyle: 'short', timeStyle: 'short' })
}

export function Branches({ onBack, onOpen }: { onBack: () => void; onOpen: (id: string) => void }) {
  const [branches, setBranches] = useState<Branch[]>([])
  const [error, setError] = useState<string | undefined>()
  const [loading, setLoading] = useState(true)

  function load() {
    setLoading(true)
    setError(undefined)
    void api
      .branches()
      .then((r) => setBranches(r.branches))
      .catch((cause: unknown) =>
        setError(cause instanceof Error ? cause.message : 'falha ao carregar'),
      )
      .finally(() => setLoading(false))
  }

  useEffect(load, [])

  const switched = (next: Branch) =>
    setBranches((current) => current.map((one) => (one.id === next.id ? next : one)))

  const columns: ReadonlyArray<Column<Branch>> = [
    {
      key: 'code',
      header: 'Código',
      className: 'text-tSmall text-text-weak',
      render: (b) => b.erpCode,
    },
    { key: 'name', header: 'Nome', render: (b) => b.name },
    {
      key: 'city',
      header: 'Cidade',
      className: 'text-tSmall text-text-weak',
      render: (b) =>
        b.city === undefined ? '—' : `${b.city}${b.state === undefined ? '' : ` / ${b.state}`}`,
    },
    {
      key: 'products',
      header: 'Produtos',
      className: 'text-tSmall',
      // Zero is a sweep that has not run, so it is not shown as an ordinary number.
      render: (b) => (
        <span className={b.products === 0 ? 'text-text-error' : 'text-text-weak'}>
          {b.products.toLocaleString('pt-BR')}
        </span>
      ),
    },
    {
      key: 'makes',
      header: 'Manipula',
      className: 'text-tSmall text-text-weak',
      render: (b) =>
        b.capabilities.length === 0
          ? 'tudo'
          : b.capabilities
              .map((id) => CAPABILITIES.find((c) => c.id === id)?.label ?? id)
              .join(', '),
    },
    {
      key: 'synced',
      header: 'Sincronizada',
      className: 'text-tSmall text-text-weak',
      render: (b) => whenSynced(b.syncedAt),
    },
    {
      key: 'verified',
      header: 'Conferência',
      render: (b) => <BranchVerificationSwitch branch={b} onSwitched={switched} compact />,
    },
  ]

  // Grouped in render rather than by a second request: the list is one row per
  // branch and the largest customer has 28 of them.
  const accounts = [...new Set(branches.map((b) => b.accountName))]

  return (
    <div className="flex flex-col gap-5 p-5 pt-0">
      <div className="flex flex-wrap items-center gap-3">
        <Button
          variant="tertiary"
          tone="neutral"
          size="small"
          leadingIcon={icons.back}
          onClick={onBack}
        >
          Fila
        </Button>
        <h1 className="text-tH4 text-text-strong">Filiais</h1>
      </div>

      {loading || error !== undefined || branches.length === 0 ? (
        <DataTable
          columns={columns}
          rows={[]}
          rowKey={(b) => b.id}
          loading={loading}
          error={error}
          onRetry={load}
          pageSize={6}
          minWidth="48rem"
          /*
           * An empty list is a job that has not run, not a customer with no
           * pharmacies -- every installation has at least one branch. Said
           * plainly so nobody reads it as "nothing to see".
           */
          empty="Nenhuma filial sincronizada ainda. O job de filiais ainda não rodou."
        />
      ) : (
        accounts.map((accountName) => (
          <section key={accountName} className="flex flex-col gap-2">
            <h2 className="text-tPSemi text-text-strong">{accountName}</h2>
            <DataTable
              columns={columns}
              rows={branches.filter((b) => b.accountName === accountName)}
              rowKey={(b) => b.id}
              onRowClick={(b) => onOpen(b.id)}
              minWidth="48rem"
            />
          </section>
        ))
      )}
    </div>
  )
}
